import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Popconfirm, Card, Row, Col, Statistic, Tag, DatePicker, Descriptions } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, PrinterOutlined, DollarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface Invoice {
  id: number;
  invoiceNo: string;
  invoiceType: string;
  invoiceDate: string;
  dueDate: string;
  accountName: string;
  branchName?: string;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  status: string;
  currency: string;
  createdByUserName?: string;
}

interface InvoiceSummary {
  totalInvoices: number;
  totalAmount: number;
  totalPaid: number;
  totalRemaining: number;
  overdueInvoices: number;
  overdueAmount: number;
}

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [form] = Form.useForm();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    loadInvoices();
    loadSummary();
  }, []);

  const showPreview = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setPreviewVisible(true);
  };

  const loadInvoices = async () => {
    try {
      setLoading(true);
      // Mock data - replace with actual API call
      const mockInvoices: Invoice[] = [
        {
          id: 1,
          invoiceNo: 'SAT-2024-001',
          invoiceType: 'SALES',
          invoiceDate: '2024-01-15T10:30:00Z',
          dueDate: '2024-02-15T10:30:00Z',
          accountName: 'ABC Müşteri A.Ş.',
          branchName: 'Merkez Ofis',
          totalAmount: 15000.50,
          paidAmount: 15000.50,
          remainingAmount: 0,
          status: 'PAID',
          currency: 'TRY',
          createdByUserName: 'Ahmet Yılmaz'
        },
        {
          id: 2,
          invoiceNo: 'SAT-2024-002',
          invoiceType: 'SALES',
          invoiceDate: '2024-01-20T14:20:00Z',
          dueDate: '2024-02-20T14:20:00Z',
          accountName: 'XYZ Ltd. Şti.',
          branchName: 'Ankara Şubesi',
          totalAmount: 8500.75,
          paidAmount: 5000.00,
          remainingAmount: 3500.75,
          status: 'PARTIALLY_PAID',
          currency: 'TRY',
          createdByUserName: 'Mehmet Kaya'
        },
        {
          id: 3,
          invoiceNo: 'SAT-2024-003',
          invoiceType: 'SALES',
          invoiceDate: '2024-01-25T16:45:00Z',
          dueDate: '2024-02-25T16:45:00Z',
          accountName: 'DEF Mağazaları',
          branchName: 'İzmir Şubesi',
          totalAmount: 12000.00,
          paidAmount: 0,
          remainingAmount: 12000.00,
          status: 'OVERDUE',
          currency: 'TRY',
          createdByUserName: 'Fatma Demir'
        }
      ];
      setInvoices(mockInvoices);
    } catch (error) {
      message.error('Faturalar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    try {
      // Mock data - replace with actual API call
      const mockSummary: InvoiceSummary = {
        totalInvoices: 125,
        totalAmount: 1250000.50,
        totalPaid: 980000.75,
        totalRemaining: 270000.75,
        overdueInvoices: 8,
        overdueAmount: 45000.25
      };
      setSummary(mockSummary);
    } catch (error) {
      console.error('Özet bilgiler yüklenirken hata oluştu:', error);
    }
  };

  const handleAdd = () => {
    setEditingInvoice(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    form.setFieldsValue({
      ...invoice,
      invoiceDate: dayjs(invoice.invoiceDate),
      dueDate: dayjs(invoice.dueDate)
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      // API call to delete invoice
      message.success('Fatura başarıyla silindi');
      loadInvoices();
    } catch (error) {
      message.error('Fatura silinirken hata oluştu');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingInvoice) {
        // API call to update invoice
        message.success('Fatura başarıyla güncellendi');
      } else {
        // API call to create invoice
        message.success('Fatura başarıyla oluşturuldu');
      }
      
      setModalVisible(false);
      loadInvoices();
    } catch (error) {
      message.error('İşlem sırasında hata oluştu');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PAID': return 'green';
      case 'PARTIALLY_PAID': return 'orange';
      case 'OVERDUE': return 'red';
      case 'SENT': return 'blue';
      case 'DRAFT': return 'default';
      case 'CANCELLED': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'PAID': return 'Ödendi';
      case 'PARTIALLY_PAID': return 'Kısmen Ödendi';
      case 'OVERDUE': return 'Vadesi Geçti';
      case 'SENT': return 'Gönderildi';
      case 'DRAFT': return 'Taslak';
      case 'CANCELLED': return 'İptal Edildi';
      default: return status;
    }
  };

  const getInvoiceTypeText = (type: string) => {
    switch (type) {
      case 'SALES': return 'Satış Faturası';
      case 'PURCHASE': return 'Alış Faturası';
      case 'CREDIT_NOTE': return 'İade Faturası';
      case 'DEBIT_NOTE': return 'Borç Dekontu';
      default: return type;
    }
  };

  const columns = [
    {
      title: 'Fatura No',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 120,
      render: (invoiceNo: string) => <strong>{invoiceNo}</strong>,
    },
    {
      title: 'Tip',
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      width: 120,
      render: (type: string) => getInvoiceTypeText(type),
    },
    {
      title: 'Tarih',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 100,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Vade',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 100,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Müşteri',
      dataIndex: 'accountName',
      key: 'accountName',
      width: 200,
    },
    {
      title: 'Şube',
      dataIndex: 'branchName',
      key: 'branchName',
      width: 120,
    },
    {
      title: 'Toplam',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 120,
      render: (amount: number, record: Invoice) => (
        <span style={{ fontWeight: 'bold' }}>
          {amount.toLocaleString('tr-TR', { style: 'currency', currency: record.currency })}
        </span>
      ),
    },
    {
      title: 'Ödenen',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      width: 120,
      render: (amount: number, record: Invoice) => (
        <span style={{ color: 'green', fontWeight: 'bold' }}>
          {amount.toLocaleString('tr-TR', { style: 'currency', currency: record.currency })}
        </span>
      ),
    },
    {
      title: 'Kalan',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      width: 120,
      render: (amount: number, record: Invoice) => (
        <span style={{ 
          color: amount > 0 ? 'red' : 'green', 
          fontWeight: 'bold' 
        }}>
          {amount.toLocaleString('tr-TR', { style: 'currency', currency: record.currency })}
        </span>
      ),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: 150,
      render: (_: any, record: Invoice) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            title="Görüntüle"
            onClick={() => showPreview(record)}
          />
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Button
            type="default"
            size="small"
            icon={<PrinterOutlined />}
            title="Yazdır"
          />
          <Popconfirm
            title="Bu faturayı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <h1>Fatura Yönetimi</h1>

      {/* Summary Cards */}
      {summary && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={4}>
            <Card>
              <Statistic
                title="Toplam Fatura"
                value={summary.totalInvoices}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Toplam Tutar"
                value={summary.totalAmount}
                formatter={(value) => value?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Ödenen"
                value={summary.totalPaid}
                formatter={(value) => value?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Kalan"
                value={summary.totalRemaining}
                formatter={(value) => value?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Vadesi Geçen"
                value={summary.overdueInvoices}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
          <Col span={4}>
            <Card>
              <Statistic
                title="Vadesi Geçen Tutar"
                value={summary.overdueAmount}
                formatter={(value) => value?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' })}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
        </Row>
      )}

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h3>Fatura Listesi</h3>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Fatura
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={invoices}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `Toplam ${total} fatura`,
        }}
        scroll={{ x: 1400 }}
      />

      <Modal
        title={editingInvoice ? 'Fatura Düzenle' : 'Yeni Fatura'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => setModalVisible(false)}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="invoiceType"
                label="Fatura Tipi"
                rules={[{ required: true, message: 'Fatura tipi seçiniz' }]}
              >
                <Select placeholder="Fatura tipi seçiniz">
                  <Select.Option value="SALES">Satış Faturası</Select.Option>
                  <Select.Option value="PURCHASE">Alış Faturası</Select.Option>
                  <Select.Option value="CREDIT_NOTE">İade Faturası</Select.Option>
                  <Select.Option value="DEBIT_NOTE">Borç Dekontu</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="invoiceDate"
                label="Fatura Tarihi"
                rules={[{ required: true, message: 'Fatura tarihi seçiniz' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dueDate"
                label="Vade Tarihi"
                rules={[{ required: true, message: 'Vade tarihi seçiniz' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="accountId"
                label="Müşteri"
                rules={[{ required: true, message: 'Müşteri seçiniz' }]}
              >
                <Select placeholder="Müşteri seçiniz">
                  <Select.Option value={1}>ABC Müşteri A.Ş.</Select.Option>
                  <Select.Option value={2}>XYZ Ltd. Şti.</Select.Option>
                  <Select.Option value={3}>DEF Mağazaları</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="branchId" label="Şube">
                <Select placeholder="Şube seçiniz" allowClear>
                  <Select.Option value={1}>Merkez Ofis</Select.Option>
                  <Select.Option value={2}>Ankara Şubesi</Select.Option>
                  <Select.Option value={3}>İzmir Şubesi</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currency" label="Para Birimi">
                <Select defaultValue="TRY">
                  <Select.Option value="TRY">TRY</Select.Option>
                  <Select.Option value="USD">USD</Select.Option>
                  <Select.Option value="EUR">EUR</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Modal */}
      <Modal
        title={`Fatura Detayı - ${selectedInvoice?.invoiceNo}`}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />}>
            Yazdır
          </Button>,
          <Button key="close" onClick={() => setPreviewVisible(false)}>
            Kapat
          </Button>,
        ]}
        width={900}
      >
        {selectedInvoice && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Fatura No" span={2}>
              <strong>{selectedInvoice.invoiceNo}</strong>
            </Descriptions.Item>
            <Descriptions.Item label="Fatura Tipi">
              {selectedInvoice.invoiceType === 'SALES' ? 'Satış Faturası' : 'Alış Faturası'}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={selectedInvoice.status === 'PAID' ? 'green' : selectedInvoice.status === 'PARTIAL' ? 'orange' : 'red'}>
                {selectedInvoice.status === 'PAID' ? 'Ödendi' : selectedInvoice.status === 'PARTIAL' ? 'Kısmi Ödemeli' : 'Ödenmedi'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Fatura Tarihi">
              {dayjs(selectedInvoice.invoiceDate).format('DD.MM.YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Vade Tarihi">
              {dayjs(selectedInvoice.dueDate).format('DD.MM.YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Cari Hesap">
              {selectedInvoice.accountName}
            </Descriptions.Item>
            <Descriptions.Item label="Şube">
              {selectedInvoice.branchName || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Toplam Tutar" span={2}>
              <strong style={{ fontSize: '18px', color: '#1890ff' }}>
                {selectedInvoice.totalAmount.toLocaleString('tr-TR', { style: 'currency', currency: selectedInvoice.currency })}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Ödenen Tutar">
              <strong style={{ color: '#52c41a' }}>
                {selectedInvoice.paidAmount.toLocaleString('tr-TR', { style: 'currency', currency: selectedInvoice.currency })}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Kalan Tutar">
              <strong style={{ color: '#ff4d4f' }}>
                {selectedInvoice.remainingAmount.toLocaleString('tr-TR', { style: 'currency', currency: selectedInvoice.currency })}
              </strong>
            </Descriptions.Item>
            <Descriptions.Item label="Oluşturan">
              {selectedInvoice.createdByUserName || '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default Invoices;
